'use client'

import { paths } from '@/config/paths'
import { CreatePositionPanel } from '@/features/my-portfolio/components/create-position-panel/CreatePositionPanel'
import { SubAccountPositions } from '@/ui/organisms/positions/SubAccountPositions'
import { useMemo } from 'react'
import type { Address } from 'viem'
import { EBorrowPosition, ELendPosition } from '../logic/useEulerPositions'

export interface EulerPositionViewProps {
  elendPositions: ELendPosition[]
  eborrowPositions: EBorrowPosition[]
}

export function EulerPositionView({ elendPositions, eborrowPositions }: EulerPositionViewProps) {
  const hasPositions = elendPositions.length > 0 || eborrowPositions.length > 0

  // Collect vault addresses and logos from positions
  const { vaultAddresses, vaultLogos } = useMemo(() => {
    const vaults = new Set<Address>()
    const logos = new Map<string, string | undefined>()

    for (const pos of elendPositions) {
      if (pos.vaultAddress) {
        vaults.add(pos.vaultAddress)
        if (pos.token?.logoURI) {
          logos.set(pos.vaultAddress.toLowerCase(), pos.token.logoURI)
        }
      }
    }

    for (const pos of eborrowPositions) {
      if (pos.collateralVaultAddress) {
        vaults.add(pos.collateralVaultAddress)
        if (pos.collateralToken?.logoURI) {
          logos.set(pos.collateralVaultAddress.toLowerCase(), pos.collateralToken.logoURI)
        }
      }
      if (pos.liabilityVaultAddress) {
        vaults.add(pos.liabilityVaultAddress)
        if (pos.liabilityToken?.logoURI) {
          logos.set(pos.liabilityVaultAddress.toLowerCase(), pos.liabilityToken.logoURI)
        }
      }
    }

    return { vaultAddresses: Array.from(vaults), vaultLogos: logos }
  }, [elendPositions, eborrowPositions])

  return (
    <div className="flex flex-col gap-6">
      {!hasPositions && (
        <CreatePositionPanel className="mb-2" title="Deposit your assets to create a position" href={paths.earn} />
      )}

      {/* Your Positions - Full view with totals and per-account breakdown */}
      {vaultAddresses.length > 0 && (
        <SubAccountPositions
          vaultAddresses={vaultAddresses}
          vaultLogos={vaultLogos}
          title="Your Positions"
          hideIfEmpty
        />
      )}
    </div>
  )
}
